import {cn} from '@/lib/utils';
import * as faceapi from 'face-api.js';
import {cloneDeep} from 'lodash';
import {useTranslations} from 'next-intl';
import Link from 'next/link';
import {redirect} from 'next/navigation';
import {JSX, useCallback, useEffect, useRef, useState} from 'react';
import useDeviceDetection from '../isMobile/isMobile';
import Tutorial from '../tutorial/Tutorial';
import './UploadImage.scss';

interface ImageOption {
  x: number;
  y: number;
  scale: number;
}

const UploadImage = (props: any): JSX.Element => {
  const {setUploaded, independent} = props;
  const t = useTranslations();

  // configurations
  const minConfidence = 0.65;
  const maxScale = 3;
  const minScore = 0.75;
  const scoreWidth = 50;
  const stepInterval = 10;

  const [boxColor, boxFont, maxBoxFont, minBoxFont] = [
    '#FF007A',
    '800 {size}px Geologica',
    40,
    20
  ];
  const [scoreFont, scoreFontColor, scoreFontFill] = [
    '700 {size}px MPLUSRounded1c',
    '#ffffff',
    '#E93290'
  ];
  const [canvasWidth, canvasHeight] = [800, 800];
  const framePadding = {top: 10, bottom: 200, left: 32, right: 32};
  const frameImgPath = '/img/smile/upload/smile-photoframe.png';
  const rawCanvasWidth = canvasWidth - framePadding.left - framePadding.right;
  const rawCanvasHeight = canvasHeight - framePadding.top - framePadding.bottom;

  const [step, setStep] = useState(0);
  const [currentOutput, setCurrentOutput] = useState<any>(null);

  // popup flag
  const [isUpload, setIsUpload] = useState(false);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [isOpenConfimClose, setIsConfimClose] = useState(false);
  const {isMobile} = useDeviceDetection();

  // ref
  const inputRef = useRef<any>(null);
  const uploadedImageRef = useRef<HTMLImageElement>(null);
  const frameImgRef = useRef<HTMLImageElement>(null);
  const smileAvgScoreRef = useRef<number>(0);
  const smileTotalScoreRef = useRef<number>(0);
  const isDragging = useRef<boolean>(false);
  const mousePosX = useRef<number>(0);
  const mousePosY = useRef<number>(0);

  // step 1
  let clock: string | number | NodeJS.Timeout | undefined;
  const defaultScale = 1;
  const [progress, setProgress] = useState(0);
  const defaultImgOption = {x: 0, y: 0, scale: 1};
  const [imgOption, setImgOption] = useState<ImageOption>(defaultImgOption);
  const tempImgOption = useRef<ImageOption>(defaultImgOption);
  const [tempScale, setTempScale] = useState<number>(defaultScale);
  const [initializing, setInitializing] = useState(true);
  const tempTouchDistance = useRef(0);
  const [isFirstEdit, setIsFirstEdit] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isAgree, setIsAgree] = useState(false);
  const [isOpenAgree, setIsOpenAgree] = useState(false);
  const [isOpenTNC, setIsOpenTNC] = useState(false);

  // setup face API
  const setupFaceApi = async () => {
    await Promise.all([
      // faceapi.nets.tinyFaceDetector.loadFromUri(
      //   "https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights"
      // ),
      faceapi.nets.ssdMobilenetv1.loadFromUri(
        'https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights'
      ),
      faceapi.nets.faceLandmark68Net.loadFromUri(
        'https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights'
      ),
      faceapi.nets.faceExpressionNet.loadFromUri(
        'https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights'
      )
    ]);

    setInitializing(false);
  };

  // select file panel
  const openSelectFilePanel = () => {
    if (initializing) {
      return;
    }
    setStep(0);
    setIsAgree(false);
    setUploaded(false);
    setIsUpload(true);
    setImgOption({x: 0, y: 0, scale: defaultScale});
    setTempScale(defaultScale);
    tempImgOption.current = {x: 0, y: 0, scale: defaultScale};
    if (independent) {
      setTimeout(() => {
        if (inputRef.current) inputRef.current.click();
      });
    }
  };

  // close popup
  const closePanel = () => {
    if (isOpenConfimClose) {
      setIsConfimClose(false);
    } else {
      if (isOpenEdit) {
        setIsConfimClose(true);
      } else {
        setIsUpload(false);
        clearTimeout(clock);
      }
    }
  };

  const clickBackdrop = (e: any) => {
    if (!e.target.closest('.preview-wrapper')) closePanel();
  };

  const getStepDom = (step: number) => {
    switch (step) {
      case 1:
        return (
          <div className="loading-container">
            <div className="loading-title">
              {t.raw('Smile.upload.popup.uploading')}
            </div>
            <div className="progress-container">
              <div
                className="progress-bar"
                style={{'--progress': `${progress}%`} as React.CSSProperties}
              ></div>
              <div className="progress">{progress}%</div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="complete-container">
            <div className="complete-result">
              <div className="complete-title">
                <img src="/img/smile/upload/title-star.svg" alt="" />
                <span>{t.raw('Smile.upload.popup.complete')}</span>
              </div>

              <div className="result-image">
                <canvas
                  id="result-canvas"
                  width={canvasWidth}
                  height={canvasHeight}
                  style={{
                    opacity: '0',
                    transition: 'opacity 0.5s ease-in-out'
                  }}
                />
              </div>
              <div className="control-btn-group">
                <div className="btn" onClick={openEdit}>
                  <img alt="img" src={`/img/smile/upload/icon-edit.svg`} />
                  <span>{t.raw('Smile.upload.popup.btnEdit')}</span>
                </div>
                <div className="btn" onClick={downloadOuput}>
                  <img alt="img" src={`/img/smile/upload/icon-download.svg`} />
                  <span>{t.raw('Smile.upload.popup.download')}</span>
                </div>
              </div>
            </div>
            <div className="btn-group">
              <div className="agree-terms">
                {!isAgree && (
                  <div
                    className="mobile-trigger"
                    onClick={() => setIsOpenAgree(true)}
                  ></div>
                )}
                <label className={cn(isAgree && 'checked')}>
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    checked={isAgree}
                    onChange={(e) => setIsAgree(e.target.checked)}
                  />
                  <div className="check">
                    <span className="material-symbols-rounded">check</span>
                    <div className="condition">
                      <div className="condition-title">
                        {t.raw('Smile.upload.popup.conditions.title')}
                      </div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: t.raw('Smile.upload.popup.conditions.content')
                        }}
                      ></div>
                    </div>
                  </div>
                  <span>
                    {t.rich('Smile.upload.popup.terms', {
                      tnc: (text) => (
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsOpenTNC(true);
                          }}
                          className="link"
                        >
                          {text}
                        </span>
                      ),
                      pp: (text) => (
                        <Link
                          prefetch={false}
                          className="link"
                          target="_blank"
                          href={t('Smile.upload.popup.privacy')}
                        >
                          {text}
                        </Link>
                      )
                    })}
                  </span>
                </label>
              </div>
              <div className="btn-wrapper">
                {!independent && (
                  <div
                    className={cn('btn back-btn')}
                    onClick={openSelectFilePanel}
                  >
                    <img alt="img" src={`/img/smile/upload/icon-refresh.svg`} />
                    <span>{t.raw('Smile.upload.popup.again')}</span>
                  </div>
                )}
                <div
                  className={cn('btn', !isAgree && 'disabled')}
                  onClick={() => isAgree && publishSmiles()}
                >
                  {t.raw('Smile.upload.popup.publish')}
                  {!isAgree && (
                    <div className="remind">
                      {t.raw('Smile.upload.popup.remind')}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {isOpenAgree && (
              <div className="condition-popup">
                <div className="preview-wrapper">
                  <div className="popup-header">
                    <div className="popup-title">
                      {t.raw('Smile.upload.popup.conditions.title')}
                    </div>
                  </div>
                  <div className="popup-body">
                    <div
                      className="content"
                      dangerouslySetInnerHTML={{
                        __html: t.raw('Smile.upload.popup.conditions.content')
                      }}
                    ></div>
                    <div className="btn-group">
                      <div
                        className="btn back-btn"
                        onClick={() => setIsOpenAgree(false)}
                      >
                        {t.raw('Smile.upload.popup.conditions.negativeBtn')}
                      </div>
                      <div
                        className="btn"
                        onClick={() => {
                          setIsAgree(true);
                          setIsOpenAgree(false);
                        }}
                      >
                        {t.raw('Smile.upload.popup.conditions.positiveBtn')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return (
          <label
            className="upload-container"
            onDragEnter={(e) => e.preventDefault()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => ondrop(e)}
          >
            <img alt="img" src={`/img/smile/upload/icon-upload.svg`} />
            <div className="upload-title">
              {t.raw('Smile.upload.popup.title')}
            </div>
            <div
              className="description"
              dangerouslySetInnerHTML={{
                __html: t.raw('Smile.upload.popup.description')
              }}
            ></div>
            <div className="btn">{t.raw('Smile.upload.popup.browse')}</div>
            <input
              type="file"
              name="upload"
              ref={inputRef}
              accept="image/png, image/jpeg, image/jpg;capture=camera, android/force-camera-workaround"
              onChange={(e) => handleFile(e.target.files)}
            />
            <Tutorial />
          </label>
        );
    }
  };

  // step 0: select file
  const ondrop = (e: any) => {
    e.preventDefault();
    inputRef.current.files = e.dataTransfer.files;
    handleFile(inputRef.current.files);
  };

  let errorTimeout = setTimeout(() => {});
  const showErrorPopup = (msg: string) => {
    clearTimeout(errorTimeout);
    setErrorMessage(msg);
    errorTimeout = setTimeout(() => {
      setErrorMessage('');
    }, 3000);
  };

  const handleFile = async (e: any) => {
    const file = e[0];
    const fileType = file.name.split('.').pop().toLowerCase();
    switch (fileType) {
      // if file is HEIC, convert to JPEG
      case 'heif':
      case 'heic':
        startCount();
        setStep(1);
        const heic2any = (await import('heic2any')).default;

        heic2any({blob: file, toType: 'image/jpeg', quality: 0.75})
          .then(function (blob: any) {
            if (Array.isArray(blob)) {
              blob = blob[0];
            }
            const uploadedImage = new Image();
            uploadedImage.onerror = () => {
              showErrorPopup(t.raw('Smile.error.unsupportedFileFormat')); // error handling
              clearTimeout(clock);
              setStep(0);
            };
            uploadedImage.onload = () => {
              setIsFirstEdit(true);
              drawPreviewSmile().catch((e) => {
                console.error(e);
                clearTimeout(clock);
                setStep(0);
              });
            };
            uploadedImage.src = URL.createObjectURL(blob);
            uploadedImageRef.current = uploadedImage;
          })
          .catch(function (error: any) {
            console.error(error);
          });
        break;
      default:
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;

          const uploadedImage = new Image();
          uploadedImage.onerror = () => {
            // error handling
            showErrorPopup(t.raw('Smile.error.unsupportedFileFormat'));
            clearTimeout(clock);
            setStep(0);
          };
          uploadedImage.onload = () => {
            setIsFirstEdit(true);
            drawPreviewSmile().catch((e) => {
              console.error(e);
              clearTimeout(clock);
              setStep(0);
            });
          };
          uploadedImage.src = result;
          uploadedImageRef.current = uploadedImage;
        };
        reader.readAsDataURL(file);
    }
  };

  const detectSmile = async (canvas: HTMLCanvasElement) => {
    const detections = await faceapi
      //.detectAllFaces(canvas, new faceapi.TinyFaceDetectorOptions())
      .detectAllFaces(
        canvas,
        new faceapi.SsdMobilenetv1Options({minConfidence})
      )
      .withFaceLandmarks()
      .withFaceExpressions();
    return detections;
  };

  // step 1: preview smile
  const drawPreviewSmile = async (isEdit?: boolean) => {
    // show result
    clearTimeout(clock);
    setProgress(100);
    setStep(2);
    clock = setTimeout(async () => {
      const canvas = document.body.querySelector(
        '#result-canvas'
      ) as HTMLCanvasElement;
      if (canvas) {
        canvas.style.opacity = '0';
        if (isEdit) {
          addImageToCanvas(
            canvas,
            uploadedImageRef.current,
            tempImgOption.current.x,
            tempImgOption.current.y,
            tempImgOption.current.scale
          );
          setImgOption({...tempImgOption.current});
        } else {
          addImageToCanvas(
            canvas,
            uploadedImageRef.current,
            defaultImgOption.x,
            defaultImgOption.y,
            defaultImgOption.scale
          );
        }
        // add average smile score
        const conf = await addSmileToCanvas(canvas);

        addFrameToCanvas(canvas);
        const total_score =
          conf.reduce((a: number, c: number) => a + c, 0) * 100;
        const avg_score = total_score / conf.length;
        smileTotalScoreRef.current = total_score;
        smileAvgScoreRef.current = avg_score;
        addScoreToCanvas(canvas);
        setCurrentOutput({outputCanvas: canvas});
        setTimeout(() => {
          canvas.style.opacity = '1';
        }, 100);
      } else {
        drawPreviewSmile(isEdit);
      }
    }, 1);
  };

  // step 2: edit smile
  const drawEditSmile = async (canvas: HTMLCanvasElement, x = 0, y = 0) => {
    if (canvas) {
      const ctx = canvas.getContext('2d');
      const image = uploadedImageRef.current;
      if (ctx) {
        addImageToCanvas(canvas, image, x, y, tempImgOption.current.scale);
        addFrameToCanvas(canvas);
        addScoreToCanvas(canvas);
      }
    }
  };

  const getImagePosition = (offsetX = 0, offsetY = 0, scale = 1) => {
    const w = rawCanvasWidth;
    const h = rawCanvasHeight;
    let [_drawWidth, _drawHeight] = [0, 0];
    const [x1, y1] = [offsetX, offsetY];
    let [x2, y2] = [0, 0];
    if (uploadedImageRef.current) {
      const {drawWidth, drawHeight} = calImageSizeInCanvas(
        uploadedImageRef.current,
        w,
        h,
        scale
      );
      [_drawWidth, _drawHeight] = [drawWidth, drawHeight];

      // calculate offset
      x2 = offsetX + drawWidth;
      y2 = offsetY + drawHeight;
    }
    return {x1, y1, x2, y2, drawWidth: _drawWidth, drawHeight: _drawHeight};
  };

  const restrictOffsets = (
    dx: number,
    dy: number,
    _scale = tempImgOption.current.scale,
    scaleOffset = false
  ) => {
    const current = tempImgOption.current;
    const x1 = current.x + dx + framePadding.left;
    const y1 = current.y + dy + framePadding.top;

    const {x2, y2, drawWidth, drawHeight} = getImagePosition(x1, y1, _scale);
    const c1 = x1 > framePadding.left;
    const c2 = x2 < canvasWidth - framePadding.right;
    const c3 = y1 > framePadding.top;
    const c4 = y2 < canvasHeight - framePadding.bottom;

    if (c1 || c2) {
      dx = scaleOffset && c2 ? rawCanvasWidth - drawWidth - current.x : 0;
    }
    if (c3 || c4) {
      dy = scaleOffset && c4 ? rawCanvasHeight - drawHeight - current.y : 0;
    }
    return {dx, dy};
  };

  const findLongestDistance = (touches: any) => {
    function calculateDistance(x1: number, y1: number, x2: number, y2: number) {
      return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    }
    let maxDistance = 0;
    for (let i = 0; i < touches.length; i++) {
      for (let j = i + 1; j < touches.length; j++) {
        const distance = calculateDistance(
          touches[i].clientX,
          touches[i].clientY,
          touches[j].clientX,
          touches[j].clientY
        );
        if (distance > maxDistance) {
          maxDistance = distance;
        }
      }
    }
    return maxDistance;
  };

  const handleMutipleTouch = (e: any, init = false) => {
    const touchPoints: any = [];
    Array.from(e.touches).forEach((el: any) => {
      touchPoints.push({clientX: el.clientX, clientY: el.clientY});
    });
    const longestDistance = findLongestDistance(touchPoints);
    if (init) tempTouchDistance.current = longestDistance;
    return longestDistance - tempTouchDistance.current;
  };

  const downImage = (e: any) => {
    e.preventDefault();
    if (e.touches?.length > 1) {
      isDragging.current = false;
      handleMutipleTouch(e, true);
    } else {
      isDragging.current = true;
      mousePosX.current = e.touches?.[0]?.clientX ?? e.clientX;
      mousePosY.current = e.touches?.[0]?.clientY ?? e.clientY;
    }
  };

  const moveImage = (e: any, canvas: HTMLCanvasElement) => {
    e.preventDefault();
    if (isDragging.current) {
      requestAnimationFrame(() => {
        const newX = e.touches?.[0]?.clientX ?? e.clientX;
        const newY = e.touches?.[0]?.clientY ?? e.clientY;
        const {dx, dy} = restrictOffsets(
          (newX - mousePosX.current) *
            Math.max(1, tempImgOption.current.scale / 0.8),
          (newY - mousePosY.current) *
            Math.max(1, tempImgOption.current.scale / 0.8)
        );
        mousePosX.current = newX;
        mousePosY.current = newY;
        tempImgOption.current.x += dx;
        tempImgOption.current.y += dy;
        const {x, y} = tempImgOption.current;
        drawEditSmile(canvas, x, y);
      });
    } else {
      if (e.touches?.length > 1) stepScale(handleMutipleTouch(e) / 35);
    }
  };

  // open edit panel
  const openEdit = async () => {
    setIsOpenEdit(true);
    if (isFirstEdit) {
      // smooth scale to 1.3
      requestAnimationFrame(async () => {
        for (let i = 1; i <= 20; i++) {
          await new Promise((resolve) => setTimeout(resolve, 10));
          drawWithScale((100 + i) / 100);
        }
      });
      setIsFirstEdit(false);
    }
  };

  const closeEditWithoutSave = () => {
    setIsConfimClose(false);
    setIsOpenEdit(false);
    tempImgOption.current = cloneDeep({
      ...imgOption
    });
    setTempScale(imgOption.scale);
  };

  const centerScaleImage = (canvas: HTMLCanvasElement, scale = 1) => {
    let [_dx, _dy] = [0, 0];

    const {w, h} = calFrameSize(canvas, _dx, _dy);
    const img = uploadedImageRef.current;
    if (img && tempImgOption.current.scale != scale) {
      const s1 = calImageSizeInCanvas(img, w, h, tempImgOption.current.scale);
      const s2 = calImageSizeInCanvas(img, w, h, scale);
      _dx -= (s2.drawWidth - s1.drawWidth) / 2;
      _dy -= (s2.drawHeight - s1.drawHeight) / 2;
    }
    return restrictOffsets(_dx, _dy, scale, true);
  };

  const drawWithScale = (_scale: number) => {
    setTempScale(_scale);
    const tempCurrent = tempImgOption.current;
    const canvas = document.body.querySelector(
      '#edit-canvas'
    ) as HTMLCanvasElement;
    const {dx, dy} = centerScaleImage(canvas, _scale);
    tempCurrent.x += dx;
    tempCurrent.y += dy;
    tempCurrent.scale = _scale;
    const {x, y} = tempCurrent;
    drawEditSmile(canvas, x, y);
  };

  const stepScale = (e: number) => {
    drawWithScale(
      Math.max(Math.min(tempImgOption.current.scale + e / 100, maxScale), 1)
    );
  };

  const editScale = (e: any) => {
    drawWithScale(e.target.value / 100 + 1);
  };

  const confirmEdit = async (e: any) => {
    setIsConfimClose(false);
    setStep(1);
    setIsOpenEdit(false);
    drawPreviewSmile(true).catch((e) => {
      console.error(e);
      clearTimeout(clock);
      setStep(2);
    });
  };

  // step 3: publish smile and download output
  const publishSmiles = async () => {
    const formData = new FormData();
    const {outputCanvas} = currentOutput;

    const element: HTMLElement | null =
      document.body.querySelector('#result-canvas');
    if (element && element.style.opacity === '0') {
      return;
    }
    const rawCanvas = document.createElement('canvas');
    rawCanvas.width = rawCanvasWidth;
    rawCanvas.height = rawCanvasHeight;
    const ctx = rawCanvas.getContext('2d');
    if (ctx) {
      drawImageProp(
        ctx,
        uploadedImageRef.current,
        rawCanvasWidth,
        rawCanvasHeight,
        tempImgOption.current.x,
        tempImgOption.current.y,
        tempImgOption.current.scale
      );
      await addSmileToCanvas(rawCanvas);
    }

    await new Promise((resolve) => {
      rawCanvas.toBlob((blob: Blob | null) => {
        if (blob) {
          formData.append('raw', blob, `smile-${Date.now()}.png`);
        }
        resolve(blob);
      }, 'image/png');
    });
    await new Promise((resolve) => {
      outputCanvas.toBlob((blob: Blob | null) => {
        if (blob) {
          formData.append('files', blob, `smile-${Date.now()}.png`);
        }
        resolve(blob);
      }, 'image/png');
    });
    const total_score = smileTotalScoreRef.current;
    const avg_score = smileAvgScoreRef.current;

    formData.append('total_score', total_score.toString());
    formData.append('avg_score', (isNaN(avg_score) ? 0 : avg_score).toString());

    startCount();
    setStep(1);
    try {
      const response = await fetch(`/api/smile/image`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      clearTimeout(clock);
      setProgress(100);
      clock = setTimeout(() => {
        if (response.ok) {
          setUploaded(true);
        } else {
          showErrorPopup(t.raw('Smile.error.uploadFailed'));
        }
        setIsUpload(false);
      }, 500);
    } catch (error: any) {
      showErrorPopup(t.raw('Smile.error.uploadFailed'));
      console.error(`Error: ${error.message}`);
      clearTimeout(clock);
      setProgress(100);
      clock = setTimeout(() => {
        setIsUpload(false);
      }, 500);
    }
  };

  const downloadOuput = () => {
    const {outputCanvas} = currentOutput;
    outputCanvas.toBlob((blob: Blob | null) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `smile.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    }, 'image/png');
  };

  // utils
  // draw canvas
  const calFrameSize = (
    canvas: HTMLCanvasElement,
    offsetX = 0,
    offsetY = 0
  ) => {
    const x = offsetX + framePadding.left;
    const y = offsetY + framePadding.top;
    const w = canvas.width - framePadding.left - framePadding.right;
    const h = canvas.height - framePadding.top - framePadding.bottom;
    return {x, y, w, h};
  };

  const calImageSizeInCanvas = (img: any, w: any, h: any, scale = 1) => {
    const imgWidth = img.width;
    const imgHeight = img.height;
    const canvasAspect = w / h;
    const imgAspect = imgWidth / imgHeight;
    let drawWidth, drawHeight;
    if (canvasAspect > imgAspect) {
      drawWidth = w * scale;
      drawHeight = (imgHeight / imgWidth) * drawWidth;
    } else {
      drawHeight = h * scale;
      drawWidth = (imgWidth / imgHeight) * drawHeight;
    }
    return {drawWidth, drawHeight};
  };

  const drawImageProp = (
    ctx: any,
    img: any,
    w: any,
    h: any,
    offsetX = 0,
    offsetY = 0,
    scale = 1
  ) => {
    if (img) {
      const {drawWidth, drawHeight} = calImageSizeInCanvas(img, w, h, scale);
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
    }
  };

  const addImageToCanvas = (
    canvas: HTMLCanvasElement,
    img: any,
    offsetX = 0,
    offsetY = 0,
    scale = 1
  ) => {
    const ctx = canvas.getContext('2d');
    const {x, y, w, h} = calFrameSize(canvas, offsetX, offsetY);

    // clear canvas
    ctx ? ctx.clearRect(0, 0, canvas.width, canvas.height) : null;

    drawImageProp(ctx, img, w, h, x, y, scale);
  };

  const addFrameToCanvas = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    drawImageProp(ctx, frameImgRef.current, canvas.width, canvas.height);
  };

  const addSmileToCanvas = async (canvas: HTMLCanvasElement) => {
    const conf: number[] = []; // smile confidence
    const ctx = canvas.getContext('2d');
    const detections = await detectSmile(canvas);

    const faceHeights: number[] = [];

    detections.forEach((detection: any) => {
      const {
        detection: {box},
        expressions
      } = detection;
      const randomScore = minScore;
      if (faceHeights.length > 0) {
        const minFaceHeight = Math.min(...faceHeights);
        if (box.height < minFaceHeight * 0.5) {
          return;
        }
      }
      faceHeights.push(box.height);

      const smileConfidence =
        expressions.happy > minScore ? expressions.happy : randomScore;

      conf.push(smileConfidence);

      if (ctx) {
        // draw square
        ctx.strokeStyle = boxColor;
        ctx.lineWidth = 2;
        ctx.strokeRect(box.x, box.y, box.width, box.height);

        // add smile score
        ctx.fillStyle = boxColor;
        const score = (smileConfidence * 100).toFixed(0);
        const size = Math.max(
          minBoxFont,
          Math.min(Math.floor(box.height / 5), maxBoxFont)
        );
        ctx.font = boxFont.replace('{size}', size.toString());
        // score position
        // ctx.fillText(
        //   score,
        //   box.x - score.length * size * 0.75,
        //   box.y + box.height
        // );
        ctx.fillText(
          score,
          box.x + box.width - score.length * size * 0.75,
          box.y + box.height + size
        );
      }
    });

    return conf;
  };

  const addScoreToCanvas = async (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    let text = '';

    if (ctx) {
      ctx.strokeStyle = scoreFontColor;
      ctx.fillStyle = scoreFontFill;
      ctx.lineWidth = 8;
      ctx.font = scoreFont.replace('{size}', (canvas.height / 14).toString());
      const avg = Math.round(smileAvgScoreRef.current);
      text = (isNaN(avg) ? 0 : avg).toString();
      // check text width
      const textWidth = ctx.measureText(text).width;
      const x =
        canvas.width - (textWidth / 2 + scoreWidth + framePadding.right);
      const y = 32 + canvas.height / 18;
      ctx.strokeText(text, x, y);
      ctx.fillText(text, x, y);
    }
    return text;
  };

  // display progress bar
  const startCount = () => {
    setProgress(0);
    clearTimeout(clock);
    clock = randomTimer(0);
    function randomTimer(count: number) {
      return setTimeout(
        () => {
          if (count < 100) {
            clock = randomTimer(count + 1);
            setProgress(count);
          }
        },
        Math.floor(Math.random() * (100 - 50 + 1)) + 50
      );
    }
  };

  const userUp = () => {
    if (isDragging.current) {
      isDragging.current = false;
    }
  };

  const handleBeforeUnload = useCallback(
    (e: any) => {
      if (isOpenEdit) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    [isOpenEdit]
  );

  useEffect(() => {
    ['beforeunload', 'pagehide'].forEach((el) =>
      window.addEventListener(el, handleBeforeUnload)
    );
    return () => {
      ['beforeunload', 'pagehide'].forEach((el) =>
        window.removeEventListener(el, handleBeforeUnload)
      );
    };
  }, [handleBeforeUnload]);

  useEffect(() => {
    document.documentElement.style.overscrollBehavior = isUpload ? 'none' : '';
    document.documentElement.style.overflow = isUpload ? 'hidden' : '';
    return () => {
      document.documentElement.style.overscrollBehavior = '';
      document.documentElement.style.overflow = '';
    };
  }, [isUpload]);

  // initialize
  const init = () => {
    const _frameImg: HTMLImageElement = new Image();
    _frameImg.src = frameImgPath;
    frameImgRef.current = _frameImg;

    (async () => {
      // load models first
      const canvas = document.createElement('canvas');
      await setupFaceApi();
      await faceapi
        //.detectAllFaces(canvas, new faceapi.TinyFaceDetectorOptions())
        .detectAllFaces(
          canvas,
          new faceapi.SsdMobilenetv1Options({minConfidence})
        )
        .withFaceLandmarks()
        .withFaceExpressions();
    })();

    ['mouseup', 'touchend'].forEach((el) =>
      window.removeEventListener(el, userUp)
    );
    ['mouseup', 'touchend'].forEach((el) =>
      window.addEventListener(el, userUp)
    );
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (isOpenEdit) {
      const openEditPanel = async () => {
        const canvas = document.body.querySelector(
          '#edit-canvas'
        ) as HTMLCanvasElement;
        let valid = false;
        if (canvas) {
          try {
            const {x, y} = tempImgOption.current;
            await drawEditSmile(canvas, x, y);
            ['mousedown', 'touchstart'].forEach((el) =>
              canvas.removeEventListener(el, downImage)
            );
            ['mousemove', 'touchmove'].forEach((el) =>
              canvas.removeEventListener(el, (e) => moveImage(e, canvas))
            );
            ['mousedown', 'touchstart'].forEach((el) =>
              canvas.addEventListener(el, downImage, {passive: false})
            );
            ['mousemove', 'touchmove'].forEach((el) =>
              canvas.addEventListener(el, (e) => moveImage(e, canvas), {
                passive: false
              })
            );
            valid = true;
          } catch (error: any) {}
        }
        if (!valid) {
          setTimeout(() => {
            openEditPanel();
          });
        }
      };
      openEditPanel();
    }
  }, [isOpenEdit]);

  return (
    <>
      {errorMessage && (
        <div id="error-msg">
          <img src="/img/smile/warn.png" style={{height: '14px'}} />
          {errorMessage}
        </div>
      )}
      <div
        className={cn('btn transition-all', initializing && 'opacity-50')}
        onClick={() => {
          if (!independent && isMobile) redirect('/smile-meter/upload');
          else openSelectFilePanel();
        }}
      >
        <img alt="img" src={`/img/smile/upload/btn-icon.svg`} />
        {t.raw(`Smile.upload.${independent ? 'select' : 'subtitle'}`)}
      </div>
      {isUpload && (
        <div
          className={`upload-preview${isOpenEdit ? ' editing' : ''}${
            independent && step == 0 ? ' hide' : ''
          }`}
        >
          <div className="preview-wrapper">
            <div className="popup-header">
              <div className="popup-title">
                {t.raw('Smile.upload.subtitle')}
              </div>
              <div className="close-btn" onClick={closePanel}>
                <img alt="img" src={`/img/smile/gallery/icon-close.svg`} />
              </div>
            </div>
            <div className="popup-body">{getStepDom(step)}</div>
            {isOpenTNC && (
              <div className="tnc-popup" onClick={clickBackdrop}>
                <div className="preview-wrapper">
                  <div className="popup-header">
                    <div
                      className="popup-title"
                      dangerouslySetInnerHTML={{
                        __html: t.raw('Smile.upload.popup.tnc.title')
                      }}
                    ></div>
                    <div
                      className="close-btn"
                      onClick={() => setIsOpenTNC(false)}
                    >
                      <img
                        alt="img"
                        src={`/img/smile/gallery/icon-close.svg`}
                      />
                    </div>
                  </div>
                  <div className="popup-body">
                    <div className="tnc-content">
                      <div className="content-wrapper">
                        {t.rich('Smile.upload.popup.tnc.content', {
                          b: (text) => <b>{text}</b>,
                          home: (text) => (
                            <Link
                              prefetch={false}
                              className="link"
                              style={{wordBreak: 'break-all'}}
                              href={'/smile-meter'}
                            >
                              {text}
                            </Link>
                          ),
                          tou: (text) => (
                            <Link
                              prefetch={false}
                              className="link"
                              target="_blank"
                              href={t('Smile.upload.popup.tnc.tou')}
                            >
                              {text}
                            </Link>
                          ),
                          pp: (text) => (
                            <Link
                              prefetch={false}
                              className="link"
                              target="_blank"
                              href={t('Smile.upload.popup.tnc.privacy')}
                            >
                              {text}
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {isOpenEdit && (
        <div
          className={`edit-preview${isOpenConfimClose ? ' confirming' : ''}`}
        >
          <div className="preview-wrapper">
            <div className="popup-header">
              <div className="popup-title">
                {t.raw('Smile.upload.popup.edit.editTitle')}
              </div>
              <div className="close-btn" onClick={() => setIsConfimClose(true)}>
                <img
                  alt="img"
                  src={
                    isMobile
                      ? `/img/smile/upload/icon-back.svg`
                      : `/img/smile/gallery/icon-close.svg`
                  }
                />
              </div>
            </div>
            <div className="popup-body">
              <div className="edit-image-container">
                <div className="edit-title">
                  {t.raw('Smile.upload.popup.edit.editDescription')}
                </div>
                <div className="edit-group">
                  <canvas
                    id="edit-canvas"
                    width={canvasWidth}
                    height={canvasHeight}
                    style={{
                      maxWidth: '400px',
                      maxHeight: '400px',
                      cursor: 'grab'
                    }}
                  ></canvas>
                  <div className="control">
                    <div
                      className="control-btn"
                      onClick={() => stepScale(-stepInterval)}
                    >
                      <span>-</span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={(maxScale - 1) * 100}
                      step={1}
                      className="slider"
                      value={(tempScale - 1) * 100}
                      onChange={editScale}
                    ></input>
                    <div
                      className="control-btn"
                      onClick={() => stepScale(stepInterval)}
                    >
                      <span>+</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="btn" onClick={confirmEdit}>
                {t.raw('Smile.upload.popup.edit.confirm')}
              </div>
            </div>
            {isOpenConfimClose && (
              <div className="confirm-close" onClick={clickBackdrop}>
                <div className="preview-wrapper">
                  <div className="popup-header">
                    <div
                      className="close-btn"
                      onClick={() => setIsConfimClose(false)}
                    >
                      <img
                        alt="img"
                        src={`/img/smile/gallery/icon-close.svg`}
                      />
                    </div>
                  </div>
                  <div className="popup-body">
                    <div className="confirm-close-container">
                      <div className="confirm-title">
                        {t.raw('Smile.upload.popup.edit.confirmTitle')}
                      </div>
                      <div className="confirm-description">
                        {t.raw('Smile.upload.popup.edit.confirmDescription')}
                      </div>
                      <div className="btn-group">
                        <div
                          className="btn back-btn"
                          onClick={closeEditWithoutSave}
                        >
                          {t.raw('Smile.upload.popup.edit.leave')}
                        </div>
                        <div className="btn" onClick={confirmEdit}>
                          {t.raw('Smile.upload.popup.edit.save')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default UploadImage;
